/**
 * Create or edit one account.
 *
 * The form changes shape with the role, because the rules genuinely differ: a
 * VP, manager or marketing employee is nothing without an employee id, while an
 * administrator or analyst has no place in the marketing hierarchy at all.
 * Showing a required field that is meaningless half the time trains people to
 * put something — anything — in it.
 *
 * Scope grants live here too, and only on an existing account: a grant is
 * layered on top of a role, so there is nothing coherent to grant before the
 * role exists.
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, KeyRound, Plus, ShieldCheck, Trash2, X } from "lucide-react";

import { api, fieldErrors } from "@/api/client";
import { EmployeeIdField } from "@/features/admin/EmployeeIdField";
import { Field, inputClass } from "@/features/auth/Register";
import { useAuthStore } from "@/store/auth";
import {
  HIERARCHY_ROLES, ROLE_LABEL, type EmployeeIdCheck, type ManagedUser,
  type Role, type ScopeGrant, type ScopeKind,
} from "@/types/api";

const ROLES: Role[] = ["ADMIN", "MANAGEMENT", "VP", "MANAGER", "EMPLOYEE"];

const ROLE_MEANING: Record<Role, string> = {
  ADMIN: "Everything, plus the ability to manage users. No employee id needed.",
  MANAGEMENT: "Every row in the warehouse, read-only. No employee id needed.",
  VP: "Their own vertical — the managers and employees reporting up to them.",
  MANAGER: "Their own team's employees, clients and revenue.",
  EMPLOYEE: "Only their own activity, clients and credits.",
};

const SCOPE_KINDS: { kind: ScopeKind; hint: string }[] = [
  { kind: "VP", hint: "person key of a VP" },
  { kind: "MANAGER", hint: "person key of a manager" },
  { kind: "EMPLOYEE", hint: "person key of an employee" },
  { kind: "CLIENT", hint: "client id" },
  { kind: "AREA", hint: "area name, exactly as recorded" },
  { kind: "BRANCH", hint: "branch name, exactly as recorded" },
];

export interface UserEditorProps {
  user: ManagedUser | null;
  onClose: () => void;
  onSaved: () => void;
}

export function UserEditor({ user, onClose, onSaved }: UserEditorProps) {
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const editing = user !== null;
  const isSelf = editing && user.id === me?.id;

  const [form, setForm] = useState({
    username: user?.username ?? "",
    display_name: user?.display_name ?? "",
    email: user?.email ?? "",
    role: (user?.role ?? "EMPLOYEE") as Role,
    employee_id: user?.employee_id ?? "",
    password: "",
    is_active: user?.is_active ?? true,
    may_see_financials: user?.may_see_financials ?? true,
    may_see_patient_pii: user?.may_see_patient_pii ?? false,
    may_export: user?.may_export ?? true,
  });
  const [check, setCheck] = useState<EmployeeIdCheck | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  // Escape closes. A modal that traps you is worse than no modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      delete next.__all__;
      return next;
    });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        username: form.username.trim(),
        display_name: form.display_name.trim(),
        email: form.email.trim(),
        role: form.role,
        employee_id: form.employee_id.trim(),
        is_active: form.is_active,
        may_see_financials: form.may_see_financials,
        may_see_patient_pii: form.may_see_patient_pii,
        may_export: form.may_export,
      };
      if (form.password) body.password = form.password;
      if (editing) {
        const { data } = await api.patch(`/admin/users/${user.id}`, body);
        return data;
      }
      const { data } = await api.post("/admin/users", body);
      return data;
    },
    onSuccess: onSaved,
    onError: (err) => {
      const found = fieldErrors(err);
      setErrors(Object.keys(found).length
        ? found
        : { __all__: ["Could not save. Try again."] });
    },
  });

  const idRequired = HIERARCHY_ROLES.includes(form.role);
  const blockedByIdCheck = idRequired && check?.ok === false;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/25"
         onClick={onClose} role="presentation">
      <aside
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={editing ? "Edit user" : "New user"}
        className="w-full max-w-md h-full bg-surface border-l border-rule
                   overflow-y-auto shadow-xl"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3
                           px-5 py-4 bg-surface border-b border-rule">
          <div>
            <div className="label">{editing ? "Edit account" : "New account"}</div>
            <h2 className="font-display font-bold text-[17px] tracking-tight">
              {editing ? user.username : "Create a login"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-alt"
                  aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <form
          className="p-5 space-y-4"
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        >
          <Field label="Username" required errors={errors.username}>
            <input
              value={form.username} onChange={(e) => set("username", e.target.value)}
              required spellCheck={false} autoComplete="off"
              className={inputClass(errors.username)}
            />
          </Field>

          <Field label="Role" required errors={errors.role}>
            <select value={form.role}
                    onChange={(e) => set("role", e.target.value as Role)}
                    disabled={isSelf}
                    className={inputClass(errors.role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
            <p className="text-[11.5px] text-ink-faint mt-1">
              {ROLE_MEANING[form.role]}
            </p>
            {isSelf && (
              <p className="text-[11.5px] text-warn mt-1">
                You cannot change your own role — otherwise one misclick could
                leave the system with no administrator.
              </p>
            )}
          </Field>

          {idRequired ? (
            <EmployeeIdField
              value={form.employee_id}
              onChange={(v) => set("employee_id", v)}
              onResolved={setCheck}
              admin required errors={errors.employee_id}
              hint="Any of the person's ids works — the identity graph collapses transfers."
            />
          ) : (
            <div className="rounded-md border border-rule bg-surface-alt px-3 py-2.5">
              <p className="text-[12.5px] text-ink-soft">
                <strong>{ROLE_LABEL[form.role]}</strong> sees the whole
                organisation, so no employee id is needed. Any id currently set
                will be cleared on save.
              </p>
            </div>
          )}

          <Field label="Display name" errors={errors.display_name}>
            <input
              value={form.display_name}
              onChange={(e) => set("display_name", e.target.value)}
              placeholder={check?.name || "filled from the hierarchy if left blank"}
              className={inputClass(errors.display_name)}
            />
          </Field>

          <Field label="Email" errors={errors.email}>
            <input
              type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inputClass(errors.email)}
            />
          </Field>

          <Field
            label={editing ? "Set a new password" : "Password"}
            required={!editing}
            errors={errors.password}
          >
            <input
              type="password" value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required={!editing} autoComplete="new-password"
              placeholder={editing ? "leave blank to keep the current one" : ""}
              className={inputClass(errors.password)}
            />
            <p className="text-[11.5px] text-ink-faint mt-1">
              At least 12 characters. There is no email on this deployment, so a
              reset means handing the new password over in person.
            </p>
          </Field>

          <fieldset className="space-y-2 pt-1 border-t border-rule">
            <legend className="label pt-2">Permissions</legend>
            <Toggle
              checked={form.is_active}
              disabled={isSelf}
              onChange={(v) => set("is_active", v)}
              label="Account active"
              hint={isSelf ? "You cannot disable your own account."
                           : "A disabled account cannot sign in but keeps its history."}
            />
            <Toggle
              checked={form.may_see_financials}
              onChange={(v) => set("may_see_financials", v)}
              label="May see financials"
              hint="Revenue, commission and payout figures."
            />
            <Toggle
              checked={form.may_see_patient_pii}
              onChange={(v) => set("may_see_patient_pii", v)}
              label="May see patient identifiers"
              warn
              hint="Separate from the role on purpose — seeing revenue and seeing a patient's name are different decisions."
            />
            <Toggle
              checked={form.may_export}
              onChange={(v) => set("may_export", v)}
              label="May export"
              hint="CSV downloads. Every export is logged with its row count."
            />
          </fieldset>

          {errors.__all__?.map((message) => (
            <p key={message} className="text-[12.5px] text-bad bg-bad-soft
                                        border border-bad/25 rounded-md px-3 py-2
                                        flex items-start gap-2">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>{message}</span>
            </p>
          ))}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn flex-1 justify-center">
              Cancel
            </button>
            <button
              type="submit"
              disabled={save.isPending || blockedByIdCheck}
              className="btn btn-primary flex-1 justify-center disabled:opacity-60"
            >
              {save.isPending ? "Saving…" : editing ? "Save changes" : "Create account"}
            </button>
          </div>
        </form>

        {editing && (
          <ScopeSection
            user={user}
            onChanged={() => queryClient.invalidateQueries({ queryKey: ["admin-users"] })}
          />
        )}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Toggle({
  checked, onChange, label, hint, warn, disabled,
}: {
  checked: boolean; onChange: (v: boolean) => void; label: string;
  hint?: string; warn?: boolean; disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-2.5 ${disabled ? "opacity-60" : ""}`}>
      <input
        type="checkbox" checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 accent-petrol"
      />
      <span className="min-w-0">
        <span className={`block text-[13px] ${warn && checked ? "text-warn font-medium" : ""}`}>
          {label}
        </span>
        {hint && <span className="block text-[11.5px] text-ink-faint">{hint}</span>}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
function ScopeSection({ user, onChanged }: { user: ManagedUser; onChanged: () => void }) {
  const [grants, setGrants] = useState<ScopeGrant[]>(user.scopes ?? []);
  const [kind, setKind] = useState<ScopeKind>("AREA");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    const { data } = await api.get<ScopeGrant[]>(`/admin/users/${user.id}/scopes`);
    setGrants(data);
    onChanged();
  };

  const add = async () => {
    setError(null);
    try {
      await api.post(`/admin/users/${user.id}/scopes`, {
        kind, value: value.trim(), reason: reason.trim(),
        expires_on: expires || null,
      });
      setValue(""); setReason(""); setExpires("");
      await reload();
    } catch (err) {
      const found = fieldErrors(err);
      setError(found.__all__?.[0] ?? found.value?.[0] ?? "Could not add that grant.");
    }
  };

  const remove = async (id: number) => {
    await api.delete(`/admin/users/${user.id}/scopes?grant=${id}`);
    await reload();
  };

  const hint = SCOPE_KINDS.find((k) => k.kind === kind)?.hint ?? "";

  return (
    <section className="px-5 pb-8 pt-2 border-t border-rule space-y-3">
      <div>
        <div className="label">Extra data scope</div>
        <p className="text-[12px] text-ink-muted mt-0.5">
          For what the hierarchy cannot express — a VP covering a peer's
          territory, an auditor scoped to one branch. Grants only widen within
          the role's ceiling, so this can never turn a manager into management.
        </p>
      </div>

      {grants.length === 0 ? (
        <p className="text-[12.5px] text-ink-faint">
          No extra grants. This account sees exactly what its role allows.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {grants.map((g) => (
            <li key={g.id}
                className="flex items-start gap-2 rounded-md border border-rule
                           bg-surface-alt px-2.5 py-2">
              <ShieldCheck size={13} className="mt-0.5 shrink-0 text-petrol" />
              <span className="min-w-0 flex-1">
                <span className="text-[12.5px]">
                  <strong>{g.kind}</strong>{" "}
                  <span className="font-mono">{g.value}</span>
                </span>
                <span className="block text-[11px] text-ink-faint">
                  {g.reason || "no reason recorded"}
                  {g.expires_on ? ` · expires ${g.expires_on}` : " · no expiry"}
                  {g.granted_by_username ? ` · by ${g.granted_by_username}` : ""}
                </span>
              </span>
              <button onClick={() => void remove(g.id)}
                      title="Revoke this grant"
                      className="p-1 rounded hover:bg-surface text-ink-muted">
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-md border border-rule p-3 space-y-2">
        <div className="flex gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value as ScopeKind)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            {SCOPE_KINDS.map((k) => (
              <option key={k.kind} value={k.kind}>{k.kind}</option>
            ))}
          </select>
          <input
            value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={hint}
            className="flex-1 text-sm bg-surface border border-rule rounded-md px-2.5 py-1.5"
          />
        </div>
        <input
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="why — this is what an auditor reads later"
          className="w-full text-sm bg-surface border border-rule rounded-md px-2.5 py-1.5"
        />
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-[12px] text-ink-muted">
            expires
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)}
                   className="text-sm bg-surface border border-rule rounded-md px-2 py-1" />
          </label>
          <button type="button" onClick={() => void add()} disabled={!value.trim()}
                  className="btn ml-auto disabled:opacity-50">
            <Plus size={13} /> Grant
          </button>
        </div>
        {error && <p className="text-[12px] text-bad">{error}</p>}
      </div>

      <p className="flex items-start gap-1.5 text-[11.5px] text-ink-faint">
        <KeyRound size={12} className="mt-0.5 shrink-0" />
        Grants and revocations are written to the audit log with the reason and
        the role ceiling that applied at the time.
      </p>
    </section>
  );
}
